import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { type InstallPromptState, dismissInstallPrompt, useInstallPrompt } from "@base/model/hooks/useInstallPrompt";
import { useEffect, useState } from "react";

/**
 * Floating install prompt banner.
 *
 * - Android: "홈 화면에 추가" button — triggers the native install dialog.
 * - iOS:     "공유 → 홈 화면에 추가" manual guide — since Safari does not
 *            support `beforeinstallprompt`.
 * - Already installed or dismissed this session: nothing rendered.
 *
 * Mount this once near the root of the app (e.g. inside `<App />`).
 */
export function InstallPromptBanner() {
  const installState = useInstallPrompt();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(
    function showBannerWhenReady() {
      if (installState.kind !== "unavailable") {
        setIsLeaving(false);
        setIsVisible(true);
        EventLogger.view(CLIENT_EVENT.PWA_INSTALL_PROMPT_VIEW, { platform: platformOf(installState.kind) });
      }
    },
    [installState.kind],
  );

  function handleDismiss() {
    EventLogger.click(CLIENT_EVENT.PWA_INSTALL_PROMPT_DISMISS, { platform: platformOf(installState.kind) });
    dismissInstallPrompt();
    setIsLeaving(true);
  }

  function handleAnimationEnd() {
    if (isLeaving) {
      setIsVisible(false);
      setIsLeaving(false);
    }
  }

  if (!isVisible) {
    return null;
  }

  const animation = isLeaving
    ? "slideDown 0.25s cubic-bezier(0.4, 0, 1, 1) both"
    : "slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both";

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div
        aria-live="polite"
        className="fixed bottom-10 left-1/2 z-[9998] w-[calc(100%-48px)] max-w-sm -translate-x-1/2"
        style={{ animation }}
        onAnimationEnd={handleAnimationEnd}
      >
        {installState.kind === "android-ready" && (
          <AndroidInstallPill onInstall={installState.prompt} onDismiss={handleDismiss} />
        )}
        {installState.kind === "ios-guide" && <IOSGuidePill onDismiss={handleDismiss} />}
      </div>
    </>
  );
}

/** 배너가 노출된 플랫폼. 설치 전환율을 안드로이드/iOS 로 나눠 보기 위한 구분자다. */
function platformOf(kind: InstallPromptState["kind"]): "android" | "ios" {
  return kind === "ios-guide" ? "ios" : "android";
}

function AndroidInstallPill({
  onInstall,
  onDismiss,
}: {
  onInstall: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-[#1a1a1a]/95 px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.32)] backdrop-blur-md">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
        <AddToHomeIcon />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-subtitle6 text-white/90">홈 화면에 추가</span>
        <span className="text-body4 text-white/50">앱처럼 빠르게 실행할 수 있어요</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg px-2 py-1.5 text-label2 text-white/40 transition-colors active:text-white/60"
        >
          나중에
        </button>
        <button
          type="button"
          onClick={onInstall}
          className="rounded-lg bg-blue-500 px-3 py-1.5 text-label2 text-white transition-opacity active:opacity-80"
        >
          추가
        </button>
      </div>
    </div>
  );
}

function IOSGuidePill({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-[#1a1a1a]/95 px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.32)] backdrop-blur-md">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
        <ShareIcon />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-subtitle6 text-white/90">홈 화면에 추가하기</span>
        <span className="text-body4 text-white/50">
          하단 <ShareInlineIcon /> 공유 버튼 → <span className="text-white/70">'홈 화면에 추가'</span> 를 탭하세요
        </span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="닫기"
        className="shrink-0 text-white/30 transition-colors active:text-white/60"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

function AddToHomeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-blue-500"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-blue-500"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function ShareInlineIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="inline-block align-middle text-white/60"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const KEYFRAMES = `
  @keyframes slideUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
  @keyframes slideDown {
    from { opacity: 1; transform: translateX(-50%) translateY(0); }
    to   { opacity: 0; transform: translateX(-50%) translateY(12px); }
  }
`;
